import supabase from "@/services/supabaseClient";
import { v4 as uuid } from "uuid";

/**
 * Path convention:
 *  <bucket>/<scope>/<userId>/<optionalSub>/<uuid>.<ext>
 * e.g. id-verifications/<userId>/<uuid>.jpg
 *      ehr-media/<patientId>/<visitId>/<uuid>.jpg
 */
export function makeObjectKey({ scope, userId, sub = "", filename }) {
  const ext = (filename?.split(".").pop() || "jpg").toLowerCase();
  const parts = [scope, userId];
  if (sub) parts.push(sub);
  parts.push(`${uuid()}.${ext}`);
  return parts.join("/");
}

/**
 * Uploads file to a private bucket.
 * Returns { key } where key is the storage object key.
 * Optional: maxBytes, allowedMimes
 */
export async function uploadObject({
  bucket,
  key,         // optional; if not provided we auto-generate
  file,
  userId,
  scope = "misc",
  sub = "",
  allowedMimes = ["image/jpeg", "image/png", "application/pdf"],
  maxBytes = 20 * 1024 * 1024, // 20MB
}) {
  if (!file) throw new Error("No file provided.");
  if (file.size > maxBytes) throw new Error("File too large.");
  if (!allowedMimes.includes(file.type)) throw new Error("Unsupported file type.");

  const finalKey = key || makeObjectKey({ scope, userId, sub, filename: file.name });

  const { error } = await supabase
    .storage
    .from(bucket)
    .upload(finalKey, file, {
      upsert: false,
      cacheControl: "3600",
      contentType: file.type || "application/octet-stream",
    });

  if (error) throw error;
  return { key: finalKey };
}

/**
 * Signed URL helper for review / display
 */
export async function signedUrl(bucket, key, expiresIn = 600) {
  const { data, error } = await supabase
    .storage
    .from(bucket)
    .createSignedUrl(key, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}